// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { getSearchHistory, SearchHistoryItem } from "@insite/client-framework/Services/AutocompleteService";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendAutocompleteOdpEvent from "@insite/client-framework/Store/CommonHandlers/SendAutocompleteOdpEvent";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { startSearch } from "@insite/client-framework/Store/CommonHandlers/StartSearch";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import AutocompleteSuggestions, {
    AutocompleteSuggestionsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteSuggestions";
import Clickable from "@insite/mobius/Clickable";
import { FormFieldClickable, FormFieldIcon, FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { FancyScrollRefProps } from "@werner/App/Components/Common/FancyScroll";
import { FloatingTooltipStyles } from "@werner/App/Components/FloatingTooltip/FloatingTooltip";
import FloatingMenuTooltip from "@werner/App/Components/Menu/FloatingMenuTooltip";
import ProductBarcodeScanner from "@werner/App/Components/ProductBarcodeScanner/ProductBarcodeScanner";
import xHeaderUpdate from "@werner/App/Handlers/XNG/Header";
import xSetSearchQuery from "@werner/App/Handlers/XNG/SearchQueryHandler";
import headerSearchInputStyles from "@werner/App/SharedStyle/HeaderSearchInputStyles";
import { CustomAppState } from "@werner/App/Store/Reducers";
import AutocompleteBrands, { AutocompleteBrandsStyles } from "@werner/Overrides/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@werner/Overrides/Widgets/Header/AutocompleteCategories";
import AutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@werner/Overrides/Widgets/Header/AutocompleteProducts";
import SearchHistory, { SearchHistoryStyles } from "@werner/Overrides/Widgets/Header/SearchHistory";
import Close from "@werner/XDL/Icons/Close";
import Search from "@werner/XDL/Icons/Search";
import { color, mergeWithCss, parseBoolean, styledScrollbar, xScreen } from "@werner/XDL/Utilities";
import { produce } from "immer";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React, { createRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement | null>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        // XNg-Changes
        isTabletScreen: state.xCommon.isTabletScreen,
        isMobileScreen: state.xCommon.isMobileScreen,
        queryState: state.xSearchQuery.query,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    sendAutocompleteOdpEvent,
    startSearch,
    // XNg-Changes
    xHeaderUpdate,
    xSetSearchQuery,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    isLoading: boolean;
    clickedProduct: boolean;
    // XNG-Changes
    autoCompleteModel?: (AutocompleteModel & { productContexts: ProductContextModel[] }) | null;
    isAutocompleteOpen?: boolean;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    autocompleteCommerceSearchWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteSuggestionsStyles?: AutocompleteSuggestionsStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    // XNg-Changes
    autocompleteColumnProductWrapper?: InjectableCss;
    floatingTooltip?: FloatingTooltipStyles;
    inputWrapper?: InjectableCss;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        mergeCss: true,
        sizeVariant: "default",
        iconProps: { src: Search, css: css`` },
        cssOverrides: {
            formInputWrapper: css`
                display: flex;
                align-items: center;
                ${FormFieldClickable as any} {
                    position: unset;
                    box-sizing: border-box;
                    width: 40px;
                    height: 40px;
                    padding: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: ${color("primary")};
                    &:hover {
                        & > span > svg {
                            transition: all 370ms ease-in-out;
                            transform: rotate(360deg);
                        }
                    }
                    &:focus {
                        box-shadow: 0 0 8px 0 ${color("primary")};
                    }
                }

                ${FormFieldIcon} {
                    pointer-events: none;
                    width: 18px;
                    height: 18px;
                    padding: 0px;
                    background: transparent;
                    border-radius: 0;
                    position: unset;
                    color: ${color("common.white")};
                }
                & > .hint-icon-wrapper {
                    position: absolute;
                    right: 40px;
                    width: 40px;
                    height: calc(100% - 4px);
                    padding: 10px;
                    border-radius: 0;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    top: unset;
                    background: ${color("common.white")};
                    & > .clear-search-btn {
                        &:hover {
                            opacity: 0.7;
                        }
                    }
                }
            `,

            inputSelect: css`
                height: 40px;
                &,
                &:focus {
                    padding: 0 12px;
                    border-radius: 3px 0 0 3px;
                }
            `,
        },
    },
    popover: {
        transitionDuration: "regular",
        toggle: false,
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
            }
            ${({ hasContent, width }: any) => (hasContent ? `min-width: ${width}px;` : `max-width: ${width}px;`)}
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 286px;
            min-width: 286px;
            min-height: 400px;
            padding-bottom: 20px;
            position: relative;
            &:after {
                content: "";
                background: ${color("common.ui4")};
                width: 1px;
                height: 100%;
                position: absolute;
                right: 0;
                top: 0;
            }
            ${xScreen("lte", "md")} {
                width: 50%;
                min-width: initial;
                min-height: unset;
                padding-bottom: 4px;
                &:after {
                    display: none;
                }
            }
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
            ${xScreen("gt", "md")} {
                ${styledScrollbar()}
            }
        `,
    },
    autocompleteCommerceSearchWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0 20px 15px 20px;
        `,
    },
    autocompleteColumnProductWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            ${({ hasContent }: any) => (hasContent ? "width: 425px" : "width: 100%")};
            padding: 16px;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    autocompleteCategoriesStyles: {
        headerText: {
            ...headerSearchInputStyles?.headerText,
        },
        link: {
            ...headerSearchInputStyles?.link,
        },
        focusedCategoryItemText: {
            ...headerSearchInputStyles?.focusedItemText,
        },
        focusedLink: {
            ...headerSearchInputStyles?.focusedLink,
        },
        categoryItemText: {
            ...headerSearchInputStyles?.itemText,
        },
        itemWrapper: {
            ...headerSearchInputStyles?.itemWrapper,
        },
    },
    autocompleteBrandsStyles: {
        headerText: {
            ...headerSearchInputStyles?.headerText,
        },
        link: {
            ...headerSearchInputStyles?.link,
        },
        focusedBrandItemText: {
            ...headerSearchInputStyles?.focusedItemText,
        },
        focusedLink: {
            ...headerSearchInputStyles?.focusedLink,
        },
        brandItemText: {
            ...headerSearchInputStyles?.itemText,
        },
        itemWrapper: {
            ...headerSearchInputStyles?.itemWrapper,
        },
    },
    autocompleteContentStyles: {
        headerText: {
            ...headerSearchInputStyles?.headerText,
        },
        link: {
            ...mergeWithCss(headerSearchInputStyles?.link, headerSearchInputStyles?.itemWrapper),
        },
        focusedLink: {
            ...mergeWithCss(headerSearchInputStyles?.focusedLink, headerSearchInputStyles?.itemWrapper),
        },
    },
    floatingTooltip: {
        // main: {
        //     css: css`
        //         &.tippy-box {
        //             border: 1px solid ${color("common.ui3")};
        //             & > .tippy-content {
        //                 max-height: calc(100vh - 200px);
        //                 scrollbar-gutter: stable;
        //             }
        //         }
        //     `,
        // },
    },
    inputWrapper: {
        css: css`
            line-height: 0; // to fix extra spacing on firefox
            position: relative;

            ${FormFieldClickable} {
                border-radius: ${({ isAutocompleteShown }: any) => (isAutocompleteShown ? "0 3px 0 0" : "0 3px 3px 0")};
            }
        `,
    },
};

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    // XNG-Changes
    private parentMenu = React.createRef<HTMLDivElement>();
    private inputWrapperRef = createRef<HTMLDivElement>();
    private fancyScrollRef = createRef<FancyScrollRefProps>();

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        // const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        this.state = {
            query: this.props.queryState,
            focusableAutocompleteItems: [],
            isLoading: false,
            clickedProduct: true,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                this.setState({ query: "" });
                this.props.xSetSearchQuery({ query: "" });
                // <XNG-Changes>
                // OOTB issue, when a user do search and return back to home page, the history not showAutocomplete update so the history popup not appear
                this.showAutocomplete = true;
                // </XNG-Changes>
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        this.showAutocomplete = false;

        this.props.startSearch({
            query: this.state.query,
            autocompleteModel: this.state.autoCompleteModel || undefined,
            searchPath: this.props.searchPath,
            goToUrl: this.goToUrl,
            searchHistoryEnabled: this.props.searchHistoryEnabled,
            searchHistoryLimit: this.props.searchHistoryLimit,
        });
    };

    // XNG-Changes: fix empty popup
    clearSearchQuery = () => {
        this.showAutocomplete = true;
        this.props.xSetSearchQuery({ query: "" });
        this.setState({ query: "" });
    };

    loadAutocomplete = debounce((query: string) => {
        this.props.getAutocompleteModel({
            query,
            onSuccess: result => {
                if (this.showAutocomplete) {
                    this.setState(
                        {
                            autoCompleteModel: {
                                ...result,
                                productContexts: (result?.products || []).map(product => ({
                                    product: {
                                        id: product.id!,
                                        productNumber: product.erpNumber,
                                        mediumImagePath: product.image,
                                        productTitle: product.title,
                                        canonicalUrl: product.url,
                                        uri: product.uri,
                                        canAddToCart: parseBoolean(product.properties.canAddToCart),
                                        manufacturerItem: product.manufacturerItemNumber,
                                        minimumOrderQty: parseInt(product.properties.minimumOrderQty || "1", 10),
                                        brand: {
                                            name: product.brandName,
                                            detailPagePath: product.brandDetailPagePath,
                                        },
                                        customerProductNumber: product.isNameCustomerOverride ? product.name : "",
                                        properties: {
                                            ...product.properties,
                                        },
                                    } as ProductModel,
                                    productInfo: {
                                        productId: product.id!,
                                        productDetailPath: product.url,
                                        qtyOrdered: parseInt(product.properties.minimumOrderQty || "1", 10),
                                        unitOfMeasure: "",
                                    },
                                    onQtyOrderedChanged: (qty: number) => {
                                        const autoCompleteModel = produce(
                                            this.state.autoCompleteModel,
                                            draftedState => {
                                                if (!draftedState) {
                                                    return;
                                                }
                                                const productIndex = draftedState.productContexts.findIndex(
                                                    p => p.product.id === product.id,
                                                );
                                                draftedState.productContexts[productIndex].productInfo.qtyOrdered = qty;
                                            },
                                        );
                                        this.setState({ autoCompleteModel });
                                    },
                                })),
                            },
                        },
                        () => {
                            this.fancyScrollRef?.current?.updateScrollable();
                        },
                    );
                    // <XNG-Changes>
                    this.updatePopoverVisibility(result);
                    // </XNG-Changes>
                    // this.openAutocomplete();}
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.setState({ query: newQuery, autoCompleteModel: null }, () => {
            this.loadAutocomplete(newQuery);
        });
        this.props.xSetSearchQuery({ query: newQuery });
        // XNG-Changes
        this.updatePopoverVisibility(this.state.autoCompleteModel);
    };

    openAutocomplete = () => {
        // XNG-Changes
        this.setState({ isAutocompleteOpen: true });
    };

    closeAutocomplete = () => {
        // XNG-Changes
        this.setState({ isAutocompleteOpen: false });
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement | null>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.focusedAutocompleteItem) {
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    this.props.xSetSearchQuery({ query: this.state.focusedSearchHistoryQuery });
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, this.doSearch);
                } else {
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        if (!this.state.clickedProduct && (this.state.autoCompleteModel?.products?.length ?? 0) > 0) {
            this.props.sendAutocompleteOdpEvent({});
        }

        this.setState({
            clickedProduct: false,
        });

        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true) => {
        const data = {
            searchQuery: this.state.query,
            searchPosition:
                this.state.autoCompleteModel?.suggestions?.findIndex(o => o.url === url) ??
                this.state.autoCompleteModel?.products?.findIndex(o => o.url === url) ??
                -1,
            attributionToken: this.state.autoCompleteModel?.attributionToken,
        };

        if (data.searchPosition >= 0) {
            this.props.sendAutocompleteOdpEvent(data);
            this.setState({ clickedProduct: true });
        }
        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        this.setState({
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        this.props.history.push(url);
    };

    // <XNG-Changes>
    isPopoverVisible(results: AutocompleteModel | undefined | null) {
        let isVisible: boolean | null = false;
        if (this.state.query && this.state.query.length > 2 && results) {
            const { categories, brands, content, products } = results;
            isVisible =
                this.props.autocompleteEnabled &&
                ((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0) ||
                    (products && products.length > 0));
        } else if (!this.state.query) {
            const searchHistoryItems = getSearchHistory();
            isVisible = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        }
        return isVisible;
    }

    updatePopoverVisibility(results: AutocompleteModel | undefined | null) {
        if (this.isPopoverVisible(results)) {
            this.openAutocomplete();
        } else {
            this.closeAutocomplete();
        }
    }

    handleInputFocus = () => {
        this.updatePopoverVisibility(this.state.autoCompleteModel);
    };

    clearSearchHistoryCallback = () => {
        this.updatePopoverVisibility(undefined);
    };
    // </XNG-Changes>

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        const {
            categories,
            brands,
            content,
            productContexts,
            suggestions,
            popularBrands,
            popularCategories,
            isRetailSearchCompletionResults,
        } = this.state.autoCompleteModel;

        return (
            <>
                {isRetailSearchCompletionResults &&
                    ((suggestions && suggestions.length > 0) ||
                        (categories && categories.length > 0) ||
                        (popularCategories && popularCategories.length > 0) ||
                        (brands && brands.length > 0) ||
                        (popularBrands && popularBrands.length > 0)) && (
                        <StyledWrapper
                            {...this.styles.autocompleteCommerceSearchWrapper}
                            className="autocomplete-commerce-search-wrapper"
                        >
                            <StyledWrapper
                                {...this.styles.autocompleteColumnWrapper}
                                className="autocomplete-column-wrapper"
                            >
                                {suggestions && suggestions.length > 0 && (
                                    <AutocompleteSuggestions
                                        suggestions={suggestions}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteSuggestionsStyles}
                                    />
                                )}
                                {((categories && categories.length > 0) ||
                                    (popularCategories && popularCategories.length > 0)) && (
                                    <AutocompleteCategories
                                        categories={categories}
                                        popularCategories={popularCategories}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteCategoriesStyles}
                                        isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                    />
                                )}
                                {((brands && brands.length > 0) || (popularBrands && popularBrands.length > 0)) && (
                                    <AutocompleteBrands
                                        brands={brands}
                                        popularBrands={popularBrands}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteBrandsStyles}
                                        isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                    />
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}

                {!isRetailSearchCompletionResults && (
                    <StyledWrapper
                        {...this.styles.autocompleteWrapper}
                        {...{
                            width: (this.inputWrapperRef.current?.getBoundingClientRect().width || 300) - 7,
                            hasContent:
                                (categories && categories.length > 0) ||
                                (brands && brands.length > 0) ||
                                (content && content.length > 0),
                        }}
                        className="autocomplete-wrapper"
                    >
                        {((categories && categories.length > 0) ||
                            (brands && brands.length > 0) ||
                            (content && content.length > 0)) && (
                            <StyledWrapper
                                {...this.styles.autocompleteColumnWrapper}
                                className="autocomplete-column-wrapper"
                            >
                                <AutocompleteCategories
                                    categories={categories}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                />
                                <AutocompleteBrands
                                    brands={brands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                />
                                <AutocompleteContent
                                    content={content}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteContentStyles}
                                />
                            </StyledWrapper>
                        )}
                        {productContexts && productContexts.length > 0 && (
                            <StyledWrapper
                                {...this.styles.autocompleteColumnProductWrapper}
                                className="autocomplete-column-wrapper"
                                {...{
                                    hasContent:
                                        (categories && categories.length > 0) ||
                                        (brands && brands.length > 0) ||
                                        (content && content.length > 0),
                                }}
                            >
                                <AutocompleteProducts
                                    products={productContexts}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteProductsStyles}
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                )}
            </>
        );
    };

    // XNG-Changes
    renderSearchAutoCompleteContent = () => {
        if (!this.state.query && this.showAutocomplete) {
            return (
                <SearchHistory
                    focusedQuery={this.state.focusedSearchHistoryQuery}
                    goToUrl={this.goToUrl}
                    extendedStyles={styles.searchHistoryStyles}
                    width={(this.inputWrapperRef.current?.getBoundingClientRect().width || 300) - 7}
                />
            );
        }
        if (this.state.query) {
            return this.renderAutocomplete();
        }
    };
    render() {
        const { styles } = this;
        const searchId = `headerSearch-${this.props.id}`;
        // XNG-Changes
        const { queryState, xHeaderUpdate, isTabletScreen, isMobileScreen } = this.props;

        const searchInput = (
            <StyledWrapper
                ref={this.inputWrapperRef}
                {...styles.inputWrapper}
                {...{ isAutocompleteShown: this.state.isAutocompleteOpen }}
            >
                <Hidden above="md">
                    <ProductBarcodeScanner />
                </Hidden>
                <TextField
                    {...styles.input}
                    ref={this.props.inputRef}
                    isAnimated={this.props.isAnimated}
                    iconClickableProps={{ ...styles.input?.iconClickableProps, onClick: this.doSearch }}
                    value={this.state.query || queryState}
                    onChange={this.handleInputChange}
                    placeholder={translate("Search Products, MFR Part # or Werner Part #")}
                    autoComplete="off"
                    clickableText={translate("submit search")}
                    id={searchId}
                    data-test-selector="headerSearchInputTextField"
                    className="search-input"
                    // XNG-Changes
                    onKeyDown={this.handlePopoverKeyDown}
                    aria-multiline="false"
                    aria-required="false"
                    onFocus={this.handleInputFocus}
                    maxLength={100}
                    hint={
                        this.state.query || queryState ? (
                            <Clickable onClick={this.clearSearchQuery} className="clear-search-btn">
                                <Icon size={10} src={Close} />
                            </Clickable>
                        ) : null
                    }
                    {...{
                        renderHintAsIcon: true,
                        renderIconNextToHint: true,
                    }}
                />
            </StyledWrapper>
        );

        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                {/* XNG-Changes: change popover with FloatingMenuTooltip */}
                <FloatingMenuTooltip
                    triggerComponent={searchInput}
                    trigger="manual"
                    offset={{ mainAxis: 0 }}
                    strategy="fixed"
                    referenceElement={this.inputWrapperRef.current}
                    isOpen={this.state.isAutocompleteOpen}
                    showArrow={false}
                    placement="bottom-start"
                    extendedStyles={styles.floatingTooltip}
                    maxWidth="none"
                    onOpenChange={open => (open ? this.handleInputFocus() : this.closeAutocomplete())}
                >
                    {this.renderSearchAutoCompleteContent()}
                </FloatingMenuTooltip>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
